// src/parsers/bom.ts

import type { RawBOMLine, ParseError, Result, BOMColumnMapping } from '../types';

export interface BOMParseOptions {
  columnMapping?: BOMColumnMapping;
  autoDetect?: boolean;
}

export interface BOMParseResult {
  lines: RawBOMLine[];
  warnings: ParseError[];
  detectedMapping?: BOMColumnMapping;
}

export async function parseBOMFile(
  content: string | ArrayBuffer,
  filename: string,
  options: BOMParseOptions = {}
): Promise<Result<BOMParseResult, ParseError[]>> {
  const warnings: ParseError[] = [];
  
  try {
    // TODO: Implement full BOM parsing
    return {
      success: true,
      data: {
        lines: [],
        warnings
      }
    };
  } catch (error) {
    return {
      success: false,
      error: [{
        file: filename,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'error'
      }]
    };
  }
}

// Parse RefDes list string into individual designators
export function parseRefDesList(refDesStr: string): string[] {
  const result: string[] = [];
  const parts = refDesStr.split(/[,;]/);
  
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    
    // Check for range pattern: R1-R10 or R1..R10
    const rangeMatch = trimmed.match(/^([A-Za-z]+)(\d+)[-.]+([A-Za-z]+)(\d+)$/);
    if (rangeMatch) {
      const prefix1 = rangeMatch[1];
      const start = parseInt(rangeMatch[2], 10);
      const prefix2 = rangeMatch[3];
      const end = parseInt(rangeMatch[4], 10);
      
      // Only expand if prefixes match
      if (prefix1 === prefix2 && !isNaN(start) && !isNaN(end) && start <= end) {
        for (let i = start; i <= end; i++) {
          result.push(`${prefix1}${i}`);
        }
        continue;
      }
    }
    
    // Single designator
    result.push(trimmed);
  }
  
  return result;
}

// Extract specifications from description text
interface ExtractedSpecs {
  voltage?: string;
  tolerance?: string;
  dielectric?: string;
  power?: string;
}

export function extractSpecifications(description: string): ExtractedSpecs {
  const specs: ExtractedSpecs = {};
  
  // Voltage: 10V, 16V, 25V, 50V, 100V, etc.
  const voltageMatch = description.match(/(\d+\.?\d*)\s*V/i);
  if (voltageMatch) {
    specs.voltage = `${voltageMatch[1]}V`;
  }
  
  // Tolerance: ±5%, 1%, 0.1%, or letter codes (J, K, M)
  const toleranceMatch = description.match(/[±]?\s*(\d+\.?\d*)\s*%/);
  if (toleranceMatch) {
    specs.tolerance = `±${toleranceMatch[1]}%`;
  } else {
    // Check for letter codes
    const letterMatch = description.match(/\b([BCDEFGJKM])\s*(tolerance|tol)?\b/i);
    if (letterMatch) {
      const letterCodes: Record<string, string> = {
        'B': '±0.1%', 'C': '±0.25%', 'D': '±0.5%',
        'F': '±1%', 'G': '±2%', 'J': '±5%',
        'K': '±10%', 'M': '±20%'
      };
      const code = letterMatch[1].toUpperCase();
      if (letterCodes[code]) {
        specs.tolerance = letterCodes[code];
      }
    }
  }
  
  // Dielectric: X7R, X5R, C0G, NP0, Y5V, etc.
  const dielectricMatch = description.match(/\b(X7R|X5R|C0G|NP0|Y5V|Z5U|X8R)\b/i);
  if (dielectricMatch) {
    specs.dielectric = dielectricMatch[1].toUpperCase();
  }
  
  // Power rating: 1/8W, 1/4W, 1/2W, 1W, etc.
  const powerMatch = description.match(/(\d+\/\d+|\d+\.?\d*)\s*W/i);
  if (powerMatch) {
    specs.power = `${powerMatch[1]}W`;
  }
  
  return specs;
}
